import React, { useRef, useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Printer, Download } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { getArrivalNoticeList } from "@/redux/ImportBooking/actions";
import { getAddressAPI } from "@/redux/Purchase/actions";
import { getMasterCompanyDetails } from "@/redux/Common/actions";
import { baseURL } from "@/config/APIConfig";
import { axiosInstance } from "@/config/NetworkUtility";

const LOGO_PROXY_PREFIX =
  import.meta.env.VITE_DISABLE_ISF_LOGO_PROXY === "true"
    ? ""
    : (import.meta.env.VITE_EXTERNAL_IMAGE_PROXY_PREFIX ??
      "https://wsrv.nl/?url=");

let cachedApiOrigin = "";
const getApiOrigin = () => {
  if (cachedApiOrigin) return cachedApiOrigin;
  try {
    cachedApiOrigin = new URL(baseURL.live).origin;
  } catch {
    cachedApiOrigin = "";
  }
  return cachedApiOrigin;
};

const isApiStaticAssetPath = (path) =>
  /^\/(?:UploadedFile|uploads?)\//i.test(path);

/** Resolve logo paths — static files live on api.shipez.ai root, not the Vite dev origin. */
const resolveMediaUrl = (src) => {
  if (!src || typeof src !== "string") return "";
  const t = src.trim();
  if (!t) return "";
  if (/^(?:https?:|data:|blob:)/i.test(t)) return t;
  if (t.startsWith("//")) return `${window.location.protocol}${t}`;

  const path = t.startsWith("/") ? t : `/${t}`;
  const apiOrigin = getApiOrigin();
  if (isApiStaticAssetPath(path) && apiOrigin) {
    return `${apiOrigin}${path}`;
  }
  return `${window.location.origin}${path}`;
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onloadend = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(blob);
  });

const resolveImageRequestUrl = (src) => {
  if (!src || src.startsWith("data:")) return src || "";
  const t = String(src).trim();
  if (/^https?:\/\//i.test(t)) return t;
  if (t.startsWith("//")) return `${window.location.protocol}${t}`;

  const path = t.startsWith("/") ? t : `/${t}`;
  const apiOrigin = getApiOrigin();
  if (isApiStaticAssetPath(path) && apiOrigin) {
    return `${apiOrigin}${path}`;
  }

  if (t.startsWith("/")) {
    try {
      return new URL(t, window.location.href).href;
    } catch {
      /* fall through */
    }
  }

  const base = baseURL.live.replace(/\/?$/, "/");
  const relPath = t.startsWith("/") ? t.slice(1) : t;
  return `${base}${relPath}`;
};

const xhrBlob = (url) =>
  new Promise((resolve, reject) => {
    try {
      const x = new XMLHttpRequest();
      x.open("GET", url, true);
      x.responseType = "blob";
      x.withCredentials = true;
      x.onload = () => {
        if (x.status >= 200 && x.status < 300) resolve(x.response);
        else reject(new Error(String(x.status)));
      };
      x.onerror = () => reject(new Error("xhr"));
      x.send();
    } catch (e) {
      reject(e);
    }
  });

const tryDrawLiveImageToDataUrl = (liveImg) => {
  if (!liveImg || !liveImg.complete || !(liveImg.naturalWidth > 0)) return "";
  try {
    const c = document.createElement("canvas");
    c.width = liveImg.naturalWidth;
    c.height = liveImg.naturalHeight;
    c.getContext("2d").drawImage(liveImg, 0, 0);
    return c.toDataURL("image/png");
  } catch {
    return "";
  }
};

const fetchImageAsDataUrlViaApi = async (src) => {
  if (!src || src.startsWith("data:")) return src || "";
  const requestUrl = resolveImageRequestUrl(src);
  const token = localStorage.getItem("authToken");
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  try {
    const { data } = await axiosInstance({
      method: "get",
      url: requestUrl,
      responseType: "blob",
      headers: { ...authHeaders },
    });
    if (data && typeof data.size === "number" && data.size > 0) {
      return await blobToDataUrl(data);
    }
  } catch {
    /* continue */
  }

  try {
    const res = await fetch(requestUrl, {
      mode: "cors",
      credentials: "include",
      headers: { ...authHeaders },
    });
    if (res.ok) return await blobToDataUrl(await res.blob());
  } catch {
    /* continue */
  }

  return "";
};

const fetchLogoViaProxy = async (absoluteHttpUrl) => {
  if (
    !LOGO_PROXY_PREFIX ||
    !absoluteHttpUrl ||
    !/^https?:\/\//i.test(absoluteHttpUrl)
  ) {
    return "";
  }
  try {
    const proxied = `${LOGO_PROXY_PREFIX}${encodeURIComponent(absoluteHttpUrl)}`;
    const r = await fetch(proxied, { mode: "cors" });
    if (!r.ok) return "";
    const blob = await r.blob();
    if (!blob?.size) return "";
    return await blobToDataUrl(blob);
  } catch {
    return "";
  }
};

const inlineImageUrlViaCanvas = (url) =>
  new Promise((resolve) => {
    const loadViaImage = (useCors) => {
      const img = new Image();
      if (useCors) img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const c = document.createElement("canvas");
          c.width = img.naturalWidth || 1;
          c.height = img.naturalHeight || 1;
          c.getContext("2d").drawImage(img, 0, 0);
          resolve(c.toDataURL("image/png"));
        } catch {
          if (!useCors) loadViaImage(true);
          else resolve("");
        }
      };
      img.onerror = () => {
        if (!useCors) loadViaImage(true);
        else resolve("");
      };
      img.src = url;
    };
    loadViaImage(false);
  });

const logoUrlToDataUrl = async (url, liveImgEl) => {
  if (!url) return "";
  if (url.startsWith("data:")) return url;

  const fromLive = tryDrawLiveImageToDataUrl(liveImgEl);
  if (fromLive) return fromLive;

  const fromApi = await fetchImageAsDataUrlViaApi(url);
  if (fromApi && String(fromApi).startsWith("data:")) return fromApi;

  const requestUrl = resolveImageRequestUrl(url);
  try {
    const r = await fetch(requestUrl, { credentials: "include", mode: "cors" });
    if (r.ok) return await blobToDataUrl(await r.blob());
  } catch {
    /* continue */
  }

  try {
    return await blobToDataUrl(await xhrBlob(requestUrl));
  } catch {
    /* continue */
  }

  const fromImg = await inlineImageUrlViaCanvas(requestUrl);
  if (fromImg) return fromImg;

  return fetchLogoViaProxy(requestUrl);
};

const readStoredCompanyDetails = () => {
  try {
    const raw = localStorage.getItem("MasterCompanyDetails");
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && typeof parsed === "object") return [parsed];
    return [];
  } catch {
    return [];
  }
};

/** API may return a single object or an array — normalize to one row. */
const normalizeCompanyRow = (source) => {
  if (!source) return null;
  if (Array.isArray(source)) return source[0] || null;
  if (typeof source === "object" && Object.keys(source).length > 0) return source;
  return null;
};

const collectLogoCandidates = (row) => {
  if (!row) return [];
  const raw = [
    row.CompanyLogo,
    row.companyLogo,
    row.CompanyLogoDark,
    row.companyLogoDark,
    row.CompanyLogoLight,
    row.companyLogoLight,
  ];
  const seen = new Set();
  return raw
    .filter(Boolean)
    .map((value) => resolveMediaUrl(String(value).trim()))
    .filter((url) => {
      if (!url || seen.has(url)) return false;
      seen.add(url);
      return true;
    });
};

const waitForDocumentImages = (root, timeoutMs = 4000) => {
  const imgs = [...(root?.querySelectorAll("img") || [])];
  if (!imgs.length) return Promise.resolve();
  return Promise.race([
    Promise.all(
      imgs.map(
        (img) =>
          new Promise((res) => {
            if (img.complete && img.naturalHeight > 0) {
              res();
              return;
            }
            img.onload = res;
            img.onerror = res;
          }),
      ),
    ),
    new Promise((res) => setTimeout(res, timeoutMs)),
  ]);
};
const LockerFreightImportBookingPrint = () => {
  const dispatch = useDispatch();
  const { MasterCompanyDetails: reduxCompanyDetails } = useSelector(
    (s) => s.common,
  );
  const { id: encodedData } = useParams();
  const bookingid = atob(encodedData);

  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoDataUrl, setLogoDataUrl] = useState("");

  const printRef = useRef();
  const [authUser] = JSON.parse(localStorage.getItem("authUser") ?? "[{}]");
  const companyId = authUser?.companyId;

  const companyInfo = useMemo(
    () =>
      normalizeCompanyRow(reduxCompanyDetails) ||
      normalizeCompanyRow(readStoredCompanyDetails()),
    [reduxCompanyDetails],
  );

  const logoCandidates = useMemo(
    () => collectLogoCandidates(companyInfo),
    [companyInfo],
  );

  const [logoCandidateIndex, setLogoCandidateIndex] = useState(0);
  const resolvedLogoUrl = logoCandidates[logoCandidateIndex] || "";
  const displayLogoSrc = logoDataUrl || resolvedLogoUrl;

  useEffect(() => {
    setLogoCandidateIndex(0);
  }, [logoCandidates]);

  // ── Fetch booking + company data ──
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (bookingid && bookingid !== 0) {
          if (authUser?.companyId) {
            await dispatch(getMasterCompanyDetails(authUser.companyId));
          }
          await _getBooking();
        }
      } catch (_) {
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Inline logo for PDF/print capture ──
  useEffect(() => {
    let cancelled = false;
    if (!resolvedLogoUrl) {
      setLogoDataUrl("");
      return undefined;
    }

    logoUrlToDataUrl(resolvedLogoUrl, null).then((dataUrl) => {
      if (!cancelled) setLogoDataUrl(dataUrl || "");
    });

    return () => {
      cancelled = true;
    };
  }, [resolvedLogoUrl]);
  const _getBooking = async () => {
    try {
      if (
        typeof bookingid === "number" ||
        (typeof bookingid === "string" && bookingid !== "0")
      ) {
        const res = await dispatch(
          getArrivalNoticeList({ BolID: bookingid, CompanyID: companyId }),
        );
        if (res) {
          await getAddressAPI(res?.customerId);
          setFormData(res);

          console.log("formData:", res);
        }
      } else if (bookingid && typeof bookingid === "object") {
        setFormData(bookingid);
      }
    } catch (_) { }
  };

  const fmt = (d) =>
    d && moment(d).isValid() ? moment(d).format("DD/MM/YYYY") : "";
  const containers = formData?.BOLDescriptionsData || [];

  /** Normalize address text and pick print font size from content length. */
  const formatAddressText = (value) => {
    if (value == null || value === "") return "";
    return String(value)
      .replace(/\r\n/g, "\n")
      .replace(/[ \t]+\n/g, "\n")
      .trim();
  };

  const addressFontSizeClass = (value) => {
    const len = formatAddressText(value).length;
    if (len > 420) return "noa-address-text--xs";
    if (len > 280) return "noa-address-text--sm";
    return "noa-address-text--md";
  };

  const partyBlocks = useMemo(
    () => [
      { key: "consignee", label: "Consignee", value: formData?.ConsigneeAddress },
      { key: "shipper", label: "Shipper", value: formData?.ShipperAddress },
      { key: "notify", label: "Notify", value: formData?.NotifyAddress },
    ],
    [formData?.ConsigneeAddress, formData?.ShipperAddress, formData?.NotifyAddress],
  );

  const ensureInlineLogo = async () => {
    const logoImg = printRef.current?.querySelector("img.noa-doc-logo");
    let inlineLogo =
      logoDataUrl ||
      (resolvedLogoUrl
        ? await logoUrlToDataUrl(resolvedLogoUrl, logoImg)
        : "");
    let prevSrc = null;

    if (logoImg && inlineLogo) {
      prevSrc = logoImg.getAttribute("src");
      logoImg.src = inlineLogo;
      logoImg.removeAttribute("crossorigin");
      try {
        await logoImg.decode();
      } catch {
        await waitForDocumentImages(printRef.current, 4000);
      }
    }

    await waitForDocumentImages(printRef.current);
    return { logoImg, inlineLogo, prevSrc };
  };

  const captureDocument = async () => {
    if (!printRef.current) return null;

    const { logoImg, inlineLogo, prevSrc } = await ensureInlineLogo();
    await new Promise((res) =>
      requestAnimationFrame(() => requestAnimationFrame(res)),
    );

    try {
      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        logging: false,
        imageTimeout: 12000,
        onclone: (_clonedDoc, clonedRoot) => {
          const img = clonedRoot.querySelector("img.noa-doc-logo");
          if (img && inlineLogo) {
            img.src = inlineLogo;
            img.removeAttribute("crossorigin");
          }
        },
      });

      return canvas.toDataURL("image/png");
    } finally {
      if (logoImg && prevSrc !== null) logoImg.src = prevSrc;
    }
  };
  // ── Print directly ──
  const handlePrint = async () => {
    try {
      const img = await captureDocument();
      if (!img) return;

      const win = window.open("", "_blank");
      if (!win) return;

      win.document.write(`<!DOCTYPE html>
<html>
  <head>
    <title>Print</title>
    <style>
      @page { size: A4; margin: 0; }
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { background: #fff; }
      img { width: 210mm; display: block; }
    </style>
  </head>
  <body>
    <img id="printImg" src="${img}" />
    <script>
      var el = document.getElementById('printImg');
      function doPrint() {
        window.focus();
        window.print();
        window.onafterprint = function() { window.close(); };
      }
      if (el.complete && el.naturalWidth > 0) {
        doPrint();
      } else {
        el.onload  = doPrint;
        el.onerror = doPrint;
      }
    </script>
  </body>
</html>`);
      win.document.close();
    } catch (e) {
      console.error("Print failed:", e);
    }
  };

  // ── Download PDF ──
  const handleDownload = async () => {
    if (!printRef.current || !formData) return;
    const btn = document.querySelector("[data-dl]");
    if (btn) {
      btn.textContent = "Generating…";
      btn.disabled = true;
    }
    try {
      const img = await captureDocument();
      if (!img) throw new Error("capture failed");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const image = new Image();
      image.src = img;
      await new Promise((res) => {
        image.onload = res;
      });
      const r = image.width / image.height;
      let w = pw,
        h = w / r;
      if (h > ph) {
        h = ph;
        w = h * r;
      }
      pdf.addImage(img, "PNG", (pw - w) / 2, (ph - h) / 2, w, h);
      pdf.save(
        `NOA_${formData.FileRefNumber || "Document"}_${moment().format("YYYY-MM-DD")}.pdf`,
      );
    } catch (_) {
      alert("PDF generation failed.");
    } finally {
      if (btn) {
        btn.textContent = "Download PDF";
        btn.disabled = false;
      }
    }
  };

  const css = `
        .noa-notice p + p { margin-top: 8pt; }
        .noa-doc-logo-wrap { min-height: 3.5rem; max-width: 180px; }
        .noa-doc-logo {
          height: 48px;
          width: auto;
          max-width: 180px;
          object-fit: contain;
          object-position: left center;
          display: block !important;
          visibility: visible !important;
          print-color-adjust: exact !important;
          -webkit-print-color-adjust: exact !important;
        }
        .noa-body-row {
          display: flex;
          align-items: stretch;
          min-width: 0;
        }
        .noa-address-col {
          width: 38%;
          flex: 0 0 38%;
          min-width: 0;
          max-width: 38%;
          display: grid;
          grid-template-rows: repeat(3, minmax(3.25em, auto));
          align-content: stretch;
          border-right: 1px solid #333;
          font-size: 7pt;
        }
        .noa-address-block {
          min-width: 0;
          display: flex;
          flex-direction: column;
          padding: 3pt 5pt;
          border-bottom: 1px solid #888;
        }
        .noa-address-block:last-child {
          border-bottom: none;
        }
        .noa-address-label {
          flex: 0 0 auto;
          font-weight: 700;
          font-size: 6.8pt;
          margin-bottom: 1.5pt;
        }
        .noa-address-text {
          flex: 1 1 auto;
          min-width: 0;
          max-width: 100%;
          width: 100%;
          white-space: pre-wrap;
          word-break: break-word;
          overflow-wrap: anywhere;
          hyphens: auto;
          line-height: 1.45;
        }
        .noa-address-text--md { font-size: 7pt; }
        .noa-address-text--sm { font-size: 6.25pt; line-height: 1.35; }
        .noa-address-text--xs { font-size: 5.75pt; line-height: 1.3; }
        .noa-detail-col {
          flex: 1 1 0;
          min-width: 0;
          display: flex;
          flex-direction: column;
        }
        .noa-detail-cell {
          min-width: 0;
          overflow: hidden;
        }
        .noa-detail-value {
          min-width: 0;
          max-width: 100%;
          word-break: break-word;
          overflow-wrap: anywhere;
          white-space: pre-wrap;
          line-height: 1.4;
          font-size: 7pt;
        }
        .noa-header-company {
          flex: 1 1 0;
          min-width: 0;
          text-align: right;
          line-height: 1.45;
          word-break: break-word;
          overflow-wrap: anywhere;
        }
    `;
  if (loading || !formData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-800">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-900 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-300 dark:bg-gray-800">
      {/* ── Toolbar ── */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-300 dark:border-gray-700 shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex justify-center gap-3">
          <button
            data-dl
            onClick={handleDownload}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg border border-gray-300 dark:border-gray-600 font-medium text-sm"
          >
            <Download className="w-4 h-4" /> Download PDF
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-5 py-2 bg-[#1a2744] hover:bg-[#0f172a] text-white rounded-lg font-medium text-sm"
          >
            <Printer className="w-4 h-4" /> Print
          </button>
        </div>
      </div>

      {/* ── Page wrapper ── */}
      <div className="flex justify-center py-8">
        <div ref={printRef} className="noa-doc-wrapper">
          {/* ════ A4 DOCUMENT ════ */}
          <div className="noa-doc w-[210mm] min-h-[297mm] bg-white font-sans text-[7pt] text-slate-800 flex flex-col shadow-[0_6px_32px_rgba(0,0,0,0.2)]">
            <div className="m-4 border border-[#333] flex flex-col">
              {/* ── HEADER ── */}
              <div className="flex items-start border-b border-[#333] px-[5mm] pt-[4mm] pb-[3mm]">
                <div className="w-[50%] flex-none noa-doc-logo-wrap">
                  {displayLogoSrc ? (
                    <img
                      src={displayLogoSrc}
                      alt={companyInfo?.CompanyName || "Logo"}
                      className="noa-doc-logo"
                      loading="eager"
                      decoding="sync"
                      onError={() => {
                        if (logoCandidateIndex < logoCandidates.length - 1) {
                          setLogoCandidateIndex((i) => i + 1);
                          setLogoDataUrl("");
                        }
                      }}
                    />
                  ) : companyInfo?.CompanyName ? (
                    <div className="font-bold text-[13pt] text-[#003087] leading-tight">
                      {companyInfo.CompanyName}
                    </div>
                  ) : null}
                </div>
                <div className="noa-header-company">
                  <div className="font-bold">{companyInfo?.CompanyName}</div>
                  {companyInfo?.ProfileAddressPrint?.split(",").map((l, i) => (
                    <div key={i}>{l.trim()}</div>
                  ))}
                  {companyInfo?.Phone && <div>Tel: {companyInfo.Phone}</div>}
                  {companyInfo?.Website && <div>{companyInfo.Website}</div>}
                </div>
              </div>

              {/* ── BODY (two columns) ── */}
              <div className="noa-body-row border-b border-[#333]">
                {/* LEFT COLUMN — flex rows grow with content; text wraps inside 38% */}
                <div className="noa-address-col">
                  {partyBlocks.map(({ key, label, value }) => {
                    const text = formatAddressText(value);
                    return (
                      <div key={key} className="noa-address-block">
                        <span className="noa-address-label">{label}</span>
                        <div
                          className={`noa-address-text ${addressFontSizeClass(value)}`}
                        >
                          {text || "\u00A0"}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* RIGHT COLUMN */}
                <div className="noa-detail-col">
                  <div className="border-b border-[#888] px-[8pt] py-[4pt]">
                    <div className="text-[20pt] text-center font-bold tracking-[0.3pt] whitespace-nowrap">
                      ADVICE OF ARRIVAL
                    </div>
                  </div>
                  <div className="m-2 border border-[#888] flex flex-col justify-center p-1 min-h-[10%]">
                    <span className="font-bold text-[7pt] text-center block">
                      Your Reference
                    </span>
                    <span
                      className="noa-detail-value text-[8pt] text-center block mt-[1pt]"
                      style={{ fontWeight: "normal" }}
                    >
                      {formData?.HBLNumber || "\u00A0"}
                    </span>
                  </div>
                  <div className="m-2 mt-0 border border-[#888] flex flex-col items-center justify-center p-1 min-h-[10%]">
                    <span className="font-bold text-[7pt] text-center block">
                      Our Reference
                    </span>
                    <span className="noa-detail-value text-[8.5pt] font-bold text-center block mt-[1pt]">
                      {formData?.FileRefNumber || "\u00A0"}
                    </span>
                  </div>
                  <div className="m-2 mt-0 border border-[#888] flex flex-col items-center justify-center px-[6pt] py-[2pt] min-h-[10%]">
                    <span className="font-bold text-[7pt] text-center block">
                      Date
                    </span>
                    <span className="text-[8.5pt] font-bold text-center block mt-[1pt]">
                      {moment().format("DD/MM/YY")}
                    </span>
                  </div>
                  {/* 2×3 grid */}
                  <div
                    className="border-t border-[#888] grid grid-cols-2 flex-1 content-start min-h-0"
                    style={{ gridTemplateRows: "repeat(3, minmax(0, auto))" }}
                  >
                    {[
                      {
                        label: "Vessel",
                        value:
                          [formData?.VesselName, formData?.Voyage]
                            .filter(Boolean)
                            .join(" / ") || "\u00A0",
                      },
                      {
                        label: "Date of Arrival",
                        value:
                          fmt(formData?.ETAPOD || formData?.ETADestination) ||
                          "\u00A0",
                      },
                      {
                        label: "Date of Departure",
                        value:
                          fmt(
                            formData?.EtdAtPol || formData?.ETDAtTransshipment,
                          ) || "\u00A0",
                      },
                      {
                        label: "Bill of Lading No.",
                        value:
                          formData?.CarrierMBLNo ||
                          formData?.HBLNumber ||
                          "\u00A0",
                      },
                      {
                        label: "Port of Loading",
                        value: formData?.PortOfLoading || "\u00A0",
                      },
                      {
                        label: "Port of Discharge",
                        value: formData?.PortOfDischarge || "\u00A0",
                      },
                    ].map(({ label, value }) => (
                      <div
                        key={label}
                        className="noa-detail-cell px-[4pt] py-[3pt] border-r border-b border-[#aaa] text-[6.8pt]"
                      >
                        <span className="font-bold text-[6.5pt] block mb-[1pt]">
                          {label}
                        </span>
                        <div className="noa-detail-value">{value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── CARGO TABLE ── */}
              <div className="border-b border-[#333]">
                <div className="flex border-b border-[#888]">
                  <div className="w-[24%] font-bold text-[6.8pt] px-[4pt] py-[2.5pt] border-r border-[#aaa]">
                    Marks and Numbers
                  </div>
                  <div className="w-[38%] font-bold text-[6.8pt] px-[4pt] py-[2.5pt] border-r border-[#aaa]">
                    Description of Goods
                  </div>
                  <div className="w-[19%] font-bold text-[6.8pt] px-[4pt] py-[2.5pt] border-r border-[#aaa] text-right">
                    Gross Kilo's
                  </div>
                  <div className="w-[19%] font-bold text-[6.8pt] px-[4pt] py-[2.5pt] text-right">
                    CBM
                  </div>
                </div>
                {containers.length ? (
                  containers.map((item, idx) => {
                    const mark = [
                      item?.ContainerNumber,
                      item?.ContTypeName,
                      item?.SealNumber ? `Seal: ${item.SealNumber}` : null,
                    ]
                      .filter(Boolean)
                      .join("\n");
                    const desc = [
                      item?.NoPackage
                        ? `${item.NoPackage} ${item?.PackageTypeName || "PKGS"}`
                        : null,
                      item?.Description ||
                      item?.CargoDescription ||
                      formData?.CargoDescription,
                    ]
                      .filter(Boolean)
                      .join("  ");
                    const kgs = Number(item?.NetKgs || item?.GrossKgs || 0);
                    const cbm = Number(item?.CBM || 0);
                    return (
                      <div
                        key={idx}
                        className="flex"
                        style={{ minHeight: "3.5em" }}
                      >
                        <div className="w-[24%] px-[4pt] py-[2.5pt] border-r border-[#ddd] text-[7pt] whitespace-pre-wrap break-words leading-[1.5]">
                          {mark || "-"}
                        </div>
                        <div className="w-[38%] px-[4pt] py-[2.5pt] border-r border-[#ddd] text-[7pt] whitespace-pre-wrap break-words leading-[1.5]">
                          {desc || "-"}
                        </div>
                        <div className="w-[19%] px-[4pt] py-[2.5pt] border-r border-[#ddd] text-[7pt] text-right">
                          {kgs
                            ? kgs.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })
                            : "-"}
                        </div>
                        <div className="w-[19%] px-[4pt] py-[2.5pt] text-[7pt] text-right">
                          {cbm ? cbm.toFixed(2) : "-"}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="flex" style={{ minHeight: "3.5em" }}>
                    <div className="w-[24%] px-[4pt] py-[2.5pt] border-r border-[#ddd]" />
                    <div className="w-[38%] px-[4pt] py-[2.5pt] border-r border-[#ddd]" />
                    <div className="w-[19%] px-[4pt] py-[2.5pt] border-r border-[#ddd]" />
                    <div className="w-[19%] px-[4pt] py-[2.5pt]" />
                  </div>
                )}
              </div>

              {/* ── PLACE OF CLEARANCE ── */}
              <div
                className="border-b border-[#333] px-[5pt] py-[3pt]"
                style={{ minHeight: "8em" }}
              >
                <span className="font-bold text-[6.8pt] block mb-[1.5pt]">
                  Place of Clearance
                </span>
                <div className="noa-detail-value mt-[3pt]">
                  {formatAddressText(formData?.PlaceOfDelivery) || ""}
                </div>
              </div>

              {/* Delivery Remarks (conditional) */}
              {formData?.DeliveryRemarks && (
                <div className="px-[5pt] py-[3pt] border-b border-[#ccc] text-[7pt]">
                  <strong>Delivery Remarks:</strong> {formData.DeliveryRemarks}
                </div>
              )}

              {/* ── RELEASE NOTICE ── */}
              <div className="noa-notice px-[6pt] pt-[6pt] text-[7.5pt] leading-[1.7] text-black text-justify">
                <p>
                  We wish to advise that the above mentioned consignment is due
                  to arrive as per the above details.&nbsp;&nbsp;Release of this
                  shipment may be obtained from the above office upon payment of
                  the charges and presentation of the Original Bill of Lading,
                  as required.
                </p>
                <p className="mt-5">
                  If you require any more information, please do not hesitate to
                  contact us.
                </p>
              </div>

              {/* ── SIGNATURE ── */}
              <div className="px-[6pt] pt-[8pt] pb-[4pt] text-[7.5pt] leading-[1.8]">
                <div>For and on Behalf of</div>
                <div>
                  <strong>{companyInfo?.CompanyName}</strong>
                </div>
              </div>

              {/* ── POWERED BY ── */}
              {companyInfo?.PoweredBy && (
                <div className="px-[5pt] py-[3pt] border-t border-[#ccc] text-[6pt] text-[#666] font-bold">
                  Powered by {companyInfo.PoweredBy}
                </div>
              )}
            </div>
          </div>

          <style>{css}</style>
        </div>
      </div>
    </div>
  );
};

export default LockerFreightImportBookingPrint;
